import { IMG, FAQS } from "../data/site";
import { PageHero, Section, Overline, EnquiryForm } from "../components/site/bits";
import { Reveal, Stagger, StaggerItem } from "../components/site/motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { AlertTriangle, Clock, Wrench, FileText, Download, ShieldAlert, MessageSquareWarning } from "lucide-react";

const REPAIRS = [
  { icon: AlertTriangle, t: "Emergency Repairs", time: "Response within 24 hours", d: "Gas leaks, no heating in winter, major water leaks, unsafe electrics, security risks.", tone: "text-red-600" },
  { icon: Clock, t: "Urgent Repairs", time: "Response within 3–5 working days", d: "Partial loss of heating or hot water, minor leaks, faulty appliances." },
  { icon: Wrench, t: "Routine Repairs", time: "Response within 28 days", d: "General wear and tear, non-urgent fixtures, cosmetic issues." },
];
const DOCS = ["Tenant Handbook (PDF)", "Complaints Policy (PDF)", "Safeguarding Policy (PDF)", "Repairs & Maintenance Guide (PDF)", "Fire Safety Guide (PDF)", "Privacy Notice (PDF)"];

export default function TenantHub() {
  return (
    <>
      <PageHero overline="Tenant Hub" lines={["Everything you", "need, in one place."]} image={IMG.livingRoom2}
        intro="Report repairs, find policies, download the tenant handbook and get support — all in one place for our residents." />

      <Section>
        <Overline>Report a Repair</Overline>
        <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">How urgent is it?</h2></Reveal>
        <Stagger className="mt-10 grid gap-6 md:grid-cols-3">
          {REPAIRS.map((r) => (
            <StaggerItem key={r.t} className="rounded-2xl border border-brand-dark/10 bg-brand-surface p-8">
              <r.icon className={`h-8 w-8 ${r.tone || "text-brand-green"}`} strokeWidth={1.5} />
              <h3 className="mt-5 font-serif text-xl text-brand-dark">{r.t}</h3>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-brand-green">{r.time}</p>
              <p className="mt-3 text-sm text-brand-slate">{r.d}</p>
            </StaggerItem>
          ))}
        </Stagger>
        <div className="mt-8 rounded-2xl bg-brand-dark p-6 text-brand-main">
          <p className="flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-red-400" /> <span>For life-threatening emergencies always call <strong className="text-brand-leaf">999</strong>. For out-of-hours emergency repairs call <strong className="text-brand-leaf">0800 123 4567</strong>.</span></p>
        </div>
      </Section>

      <Section className="bg-brand-surface/60">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal><div className="rounded-3xl border border-brand-dark/10 bg-brand-surface p-8 md:p-10">
              <h2 className="font-serif text-3xl text-brand-dark">Report a repair</h2>
              <div className="mt-8">
                <EnquiryForm kind="repair" testid="repair-form" submitLabel="Submit repair request" fields={[
                  { name: "name", label: "Your name", required: true, placeholder: "Full name" },
                  { name: "phone", label: "Phone", type: "tel", required: true, placeholder: "07000 000000" },
                  { name: "address", label: "Property address", required: true, placeholder: "Your address" },
                  { name: "priority", label: "Priority", type: "select", required: true, placeholder: "Select priority", options: ["Emergency", "Urgent", "Routine"] },
                  { name: "message", label: "Describe the repair", type: "textarea", required: true, placeholder: "What needs fixing?" },
                ]} />
              </div>
            </div></Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 space-y-6">
            <div className="rounded-2xl border border-brand-dark/10 bg-brand-surface p-6">
              <ShieldAlert className="h-7 w-7 text-brand-green" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif text-xl text-brand-dark">Safeguarding</h3>
              <p className="mt-2 text-sm text-brand-slate">If you or someone you know is at risk of harm, tell your support worker or call our safeguarding line immediately on 024 7699 5724.</p>
            </div>
            <div className="rounded-2xl border border-brand-dark/10 bg-brand-surface p-6">
              <MessageSquareWarning className="h-7 w-7 text-brand-green" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif text-xl text-brand-dark">Complaints</h3>
              <p className="mt-2 text-sm text-brand-slate">We take all concerns seriously. Raise a complaint with your support worker or email complaints@shelterease.org.uk — we respond within 5 working days.</p>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Overline>Policies & Downloads</Overline>
            <Stagger className="mt-8 space-y-3">
              {DOCS.map((d) => (
                <StaggerItem key={d}>
                  <a href="#" data-testid={`download-${d}`} className="group flex items-center justify-between rounded-xl border border-brand-dark/10 bg-brand-surface px-6 py-4 transition-colors hover:border-brand-green/40">
                    <span className="flex items-center gap-3 text-brand-dark"><FileText className="h-5 w-5 text-brand-green" />{d}</span>
                    <Download className="h-5 w-5 text-brand-slate transition-colors group-hover:text-brand-green" />
                  </a>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="lg:col-span-6">
            <Overline>Tenant FAQs</Overline>
            <Accordion type="single" collapsible className="mt-6">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`t-${i}`} className="border-brand-dark/10">
                  <AccordionTrigger data-testid={`tenant-faq-${i}`} className="text-left font-serif text-lg text-brand-dark hover:text-brand-green hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-brand-slate">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>
    </>
  );
}
